import React, { useEffect, useState } from 'react';
import { Form, Input, Radio, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import './PersonalInfoEdit.scss';
import DrawerFormContainer from 'components/drawerFormContainer/DrawerFormContainer';
import { fetchAndUpdateProfileWithPartialProperties } from 'services/profileService';
import type { CommonOption } from 'types/common';
import { LoyaltyAccount } from 'types/dto/profile';
// import { useSession } from 'context/SessionContext';
import { SecurityUtils } from 'utils/securityUtils';

interface PersonalInfoEditProps {
  visible: boolean;
  onClose: () => void;
  onSave?: () => void;
  loyaltyAccount?: LoyaltyAccount;
  titleOptions?: CommonOption[];
}

export interface PersonalInfoFormData {
  title: string;
  firstName: string;
  lastName: string;
  // birthDay: string;
}

const PersonalInfoEdit: React.FC<PersonalInfoEditProps> = ({
  visible,
  onClose,
  onSave,
  loyaltyAccount,
  titleOptions = [],
}) => {
  const { t } = useTranslation('profile');
  const { t: tCommon } = useTranslation('common');
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // const { session } = useSession();

  useEffect(() => {
    if (visible) {
      form.resetFields();
    }
  }, [form, visible]);

  const handleValuesChange = (changedValues: any) => {
    const fieldNames = Object.keys(changedValues);
    if (fieldNames.length > 0) {
      const fieldsToUpdate = fieldNames.map(name => ({
        name: name as any,
        errors: [],
      }));
      form.setFields(fieldsToUpdate);
    }
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields();
      // console.log('Personal info saved:', values);

      await fetchAndUpdateProfileWithPartialProperties(loyaltyAccount?.unique_id || '',  {
        salutation: values.title,
        first_name: values.firstName,
        last_name: values.lastName,
      });

      onSave?.();
    } catch (error) {
      console.error('Validation failed:', error);
      notification.error({
        message: tCommon('error.updatePersonalInfoFailed'),
        description: (error as Error).message,
        duration: 1.5,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DrawerFormContainer
      title={t('personalInfoEdit.title')}
      visible={visible}
      onClose={() => {
        onClose();
      }}
      onSave={handleSave}
      isSubmitting={isSubmitting}
    >
      <Form<PersonalInfoFormData>
        form={form}
        layout="vertical"
        validateTrigger="onSubmit"
        onValuesChange={handleValuesChange}
        initialValues={{
          title: loyaltyAccount?.salutation
            ? loyaltyAccount.salutation.toUpperCase()
            : '',
          firstName: loyaltyAccount?.first_name || '',
          lastName: loyaltyAccount?.last_name || '',
          // birthday:
          //   loyaltyAccount?.birth_month
          //     ? `${loyaltyAccount?.birth_month}/****`
          //     : '',
        }}
        className="personal-info-form"
      >
        <Form.Item
          name="title"
          label={t('personalInfoEdit.titleLabel')}
          rules={[
            {
              required: true,
              message: t('personalInfoEdit.titleRequired'),
            },
          ]}
        >
          <Radio.Group className="title-radio-group">
            {titleOptions &&
              titleOptions.map((option) => (
                <Radio key={option.value} value={option.value}>
                  {option.label}
                </Radio>
              ))}
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="firstName"
          label={t('personalInfoEdit.firstNameLabel')}
          getValueFromEvent={(e) => SecurityUtils.sanitize(e.target.value)}
          rules={[
            {
              required: true,
              message: t('personalInfoEdit.firstNameRequired'),
            },
          ]}
        >
          <Input placeholder={t('personalInfoEdit.firstNamePlaceholder')} />
        </Form.Item>

        <Form.Item
          name="lastName"
          label={t('personalInfoEdit.lastNameLabel')}
          getValueFromEvent={(e) => SecurityUtils.sanitize(e.target.value)}
          rules={[
            {
              required: true,
              message: t('personalInfoEdit.lastNameRequired'),
            },
          ]}
        >
          <Input placeholder={t('personalInfoEdit.lastNamePlaceholder')} />
        </Form.Item>

        {/* <Form.Item
          name="birthday"
          label={t('personalInfoEdit.birthdayLabel')}
          rules={[
            {
              required: true,
              message: t('personalInfoEdit.birthdayRequired'),
            },
          ]}
        >
          <Input
            placeholder={t('personalInfoEdit.birthdayPlaceholder')}
            disabled
          />
        </Form.Item> */}
      </Form>
    </DrawerFormContainer>
  );
};

export default PersonalInfoEdit;